'use client'

import { useEffect, useRef, useState } from 'react'
import Portal from './Portal'
import {
  FaDiscord,
  FaEdit,
  FaLock,
  FaSearch,
  FaTimes,
  FaCheck,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import { loras } from '../helpers/config'
import { poseOwnThumb, poseThumb } from '../helpers/poseThumb'
import { poseListModel, supportsCustomPose } from '../helpers/helpers'
import { safeGetItem, safeSetItem } from '../helpers/safeStorage'
import { useAuth } from '../contexts/AuthContext'
import { useUpgradeGate } from '../contexts/UpgradeGateContext'

// A pose's required plan is DATA (the loras table), so the upgrade modal is opened with its
// tier overridden rather than carrying one registry entry per tier. 'Pro'/'Studio' both mean the
// plan users see as Pro; anything unrecognised falls through to the registry's own default.
const PLAN_TO_FEATURE_TIER = {
  Plus: 'plus',
  Premium: 'premium',
  Pro: 'pro',
  Studio: 'pro',
}

const LoraModal = ({
  isOpen,
  closeModal,
  lora,
  setLora,
  lorasList,
  setLorasList,
  subscription,
  model,
  customPoseImage,
  setCustomPoseImage,
  setIsLimitModalOpen,
  setErrorText,
  setShowAttributes,
  isVideo = false,
  user = false,
}) => {
  const { t } = useTranslation()
  const { isPlusOrHigher, isPremiumOrHigher, isStudioOrHigher } = useAuth()
  const { openUpgrade } = useUpgradeGate()
  const fileInputRef = useRef(null)

  const [selectedImage, setSelectedImage] = useState(null)
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [filter, setFilter] = useState(
    () => safeGetItem('filter') || 'Straight',
  )
  const [filteredLoras, setFilteredLoras] = useState([])
  const [searchedLoras, setSearchedLoras] = useState([])

  const [searchInput, setSearchInput] = useState('')
  const [selectMode, setSelectMode] = useState(false)
  const isPonyModel =
    typeof model === 'string' && model.toLowerCase().includes('pony')
  // XL 2 models (Cinematic Flame / Anime Flame) also support Multiple Poses
  const isFlameModel =
    typeof model === 'string' && model.toLowerCase().includes('flame')
  const supportsMultiPose = isPonyModel || isFlameModel

  useEffect(() => {
    safeSetItem('filter', filter)
  }, [filter])

  useEffect(() => {
    if (lorasList?.length > 0) {
      setSelectMode(true)
    }
  }, [lorasList])

  useEffect(() => {
    // allow the “Custom” pose through, where the model can render it
    if (lora === 'Custom') {
      if (!supportsCustomPose(model)) setLora('Default')
      return
    }

    // find the selected lora in our list
    const current = loras.find((item) => item.title === lora)

    // if it doesn’t exist or is not enabled for this model, reset to Default
    if (
      !current ||
      (current.enabled && !current.enabled.includes(poseListModel(model)))
    ) {
      setLora('Default')
    }
  }, [model, lora, setLora, loras])

  useEffect(() => {
    if (!supportsMultiPose) {
      setLorasList?.([])
      setSelectMode(false)
    }
  }, [model])

  useEffect(() => {
    if (!filter || filter === 'All') {
      setFilteredLoras(loras)
    } else {
      const filtered = loras.filter(
        (lora) =>
          lora?.categories?.includes(filter) || lora?.title === 'Default',
      )
      setFilteredLoras(filtered)
    }
  }, [filter])

  useEffect(() => {
    function filterImagesBySearchTerm(images, searchTerm) {
      return images.filter((image) =>
        image?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
      )
    }

    if (searchInput && searchInput.length > 0) {
      setSearchedLoras(filterImagesBySearchTerm(filteredLoras, searchInput))
    } else {
      setSearchedLoras(filteredLoras)
    }
  }, [filteredLoras, searchInput])

  useEffect(() => {
    if (isOpen) setShouldRender(true)
  }, [isOpen])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset'
  }, [isOpen])

  // NEW: toggle add/remove for multi-select with max 3
  const toggleLoraInList = (title) => {
    setCustomPoseImage(null)
    setLora('Default')
    const already = lorasList.includes(title)
    if (already) {
      setLorasList(lorasList.filter((t) => t !== title))
    } else {
      if (lorasList.length >= 3) {
        return
      }
      setLorasList([...lorasList, title])
    }
  }

  const handleImageClick = (e, image) => {
    e.preventDefault()
    if (!canSelectImage(image.plan)) {
      // Was router.push('/upgrade') — a hard navigation out of the create session that never
      // said which pose was locked or what unlocks it.
      openUpgrade('pose', {
        overrides: {
          name: t(image.title),
          ...(PLAN_TO_FEATURE_TIER[image.plan]
            ? { plan: PLAN_TO_FEATURE_TIER[image.plan] }
            : {}),
        },
      })
      return
    }

    // NEW: when multi-pose model + select mode, update lorasList instead of setLora
    if (supportsMultiPose && selectMode) {
      toggleLoraInList(image.title)
      return
    }

    // default single-select behavior
    if (canSelectImage(image.plan)) {
      setSelectedImage(image)
      setLora(image.title)
      closeModal()
    }
  }

  useEffect(() => {
    const current = loras.find((item) => item.title === lora)

    if (current?.categories?.includes('Gay')) {
      setShowAttributes(false)
    }
  }, [lora, setShowAttributes])

  const handleCustomPose = (e) => {
    if (!isStudioOrHigher) {
      openUpgrade('customPose')
      setLora('Default')
      closeModal()
      return
    }

    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = function (event) {
        const originalBase64 = event.target.result

        const img = new Image()
        img.src = originalBase64

        img.onload = () => {
          // Create a canvas element
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height

          // Draw the image onto the canvas
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)

          // Get the compressed JPEG image as a base64 string
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6)

          // Set this compressed base64 string to state
          setCustomPoseImage(compressedBase64)
        }
      }

      reader.readAsDataURL(file)
      setLora('Custom')
      closeModal()
    }
  }

  const getColorForPlan = (plan = 'Free') => {
    switch (plan) {
      case 'Pro':
      case 'Studio':
        return '#FFB419'
      case 'Premium':
        return '#7ED6FF'
      case 'Plus':
        return '#E54B00'
      default:
        return '#FFFFFF'
    }
  }

  const canSelectImage = (imagePlan) => {
    switch (imagePlan) {
      case 'Free':
        return true
      case 'Plus':
        return isPlusOrHigher
      case 'Premium':
        return isPremiumOrHigher
      case 'Pro':
      case 'Studio':
        return isStudioOrHigher
      default:
        return false
    }
  }

  if (!shouldRender) {
    return null
  }

  return (
    <Portal>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeModal()
          }
        }}
        className={`fixed z-[110] top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center ${
          isOpen ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-200 ${
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <div
          className={`bg-[var(--middle-bg-color)] h-3/4 md:h-2/3 w-3/4 md:w-2/3 flex flex-col rounded-lg shadow-2xl p-6 text-gray-100 space-y-4 relative transform transition-all duration-200 ${
            isOpen ? 'scale-100 opacity-100' : 'scale-70 opacity-0'
          }`}
        >
          <header>
            <h2 className="text-xl font-semibold text-shadow"> {t(lora)}</h2>

            <button
              onClick={closeModal}
              className="absolute right-2 top-2 m-4 text-white"
              style={{ fontSize: '1.5rem' }}
            >
              <FaTimes />
            </button>
          </header>
          <hr className="border-gray-800" />

          <div className="flex items-center ">
            <div className={'relative md:w-1/3 w-1/3 self-end'}>
              <input
                type="search"
                placeholder="Search"
                className="border border-gray-500 rounded-full pl-10 pr-3 py-2 w-full text-white bg-[var(--middle-bg-color)] focus:border-white focus:outline-none"
                onChange={(e) => setSearchInput(e.target.value)}
                value={searchInput}
              />

              {searchInput && (
                <span className="absolute right-3.5 top-3.5 ml-3.5 text-gray-400 md:hidden block">
                  <FaTimes onClick={() => setSearchInput('')} />
                </span>
              )}
              <span className="absolute left-0 top-3 ml-3.5 text-gray-400">
                <FaSearch />
              </span>
            </div>

            {/* Filter dropdown */}
            <div className="relative ml-2 md:w-1/5 w-1/3">
              <select
                id="filter"
                value={filter}
                onChange={(e) => {
                  const value = e.target.value
                  setFilter(value)
                }}
                className="border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[#0F0D14] focus:border-white focus:outline-none appearance-none"
              >
                <option value="Straight"> {t('Straight')}</option>
                <option value="Lesbian"> {t('Lesbian')}</option>
                <option value="Gay"> {t('Gay')}</option>
                <option value="All"> {t('All')}</option>
              </select>

              {/* Dropdown arrow */}
              <span className="absolute inset-y-0 text-lg right-0 flex items-center pr-5 pointer-events-none text-gray-400 -translate-y-1">
                ⌄
              </span>
            </div>

            <div className="relative ml-2 md:w-1/5 w-1/3">
              <button
                onClick={() => {
                  if (!isStudioOrHigher) {
                    openUpgrade('multiPose')

                    setLorasList?.([])
                    setSelectMode(false)
                    closeModal()
                    return
                  } else if (!supportsMultiPose) {
                    setErrorText(
                      'Multiple Poses is only available for XL+ and XL 2 Styles.',
                    )
                    setIsLimitModalOpen(true)

                    setLorasList?.([])
                    setSelectMode(false)
                    closeModal()
                    return
                  } else if (selectMode) {
                    // User pressed "Cancel": clear all selected loras
                    setLorasList?.([])
                  }
                  setSelectMode((v) => !v)
                }}
                className="relative border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[#0F0D14] focus:border-white focus:outline-none transition-transform duration-200 flex items-center justify-center"
              >
                <span className="">{selectMode ? 'Cancel' : 'Select'}</span>
                {selectMode && (
                  <span className="ml-2 text-xs opacity-80">
                    {lorasList.length}/3
                  </span>
                )}
                {!selectMode && (
                  <span className="bg-[var(--main-bg-color)] text-white text-xs font-bold px-2 py-1 rounded-full absolute top-0 right-0 transform translate-x-2/3 -translate-y-1/2 transition-opacity duration-200 group-hover:opacity-0">
                    {t('new')}
                  </span>
                )}
              </button>
            </div>

            {user && (
              <Link
                href="https://discord.gg/cZuJFFb34e"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white rounded-xl focus:outline-none font-semibold shadow-lg flex items-center justify-center py-2 px-3 ml-auto hover:scale-[1.03] transition-transform duration-200"
              >
                <FaDiscord className="text-[var(--main-bg-color)] " size={24} />
                <p className="text-sm flex items-center">
                  <span className="md:inline hidden ml-1 text-xs">
                    Requests
                  </span>
                </p>
              </Link>
            )}
          </div>

          <main className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1 overflow-y-auto overflow-x-hidden flex-grow p-2">
            {!isVideo &&
              !model.includes('Happy') &&
              !selectMode &&
              supportsCustomPose(model) && (
                <div
                  className={`m-1 relative rounded-xl transition-all duration-200 ease-in-out cursor-pointer border-2 border-transparent hover:border-gray-800 hover:scale-[1.03]`}
                >
                  {customPoseImage ? (
                    <>
                      <img
                        src={customPoseImage}
                        alt={'Character image'}
                        className={`w-full h-full object-cover rounded-xl aspect-square`}
                        onClick={() => {
                          setLora('Custom')
                          closeModal()
                        }}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          fileInputRef.current.click()
                        }}
                        className="absolute top-0 right-0 p-2 bg-black bg-opacity-60 rounded-bl-xl cursor-pointer"
                      >
                        <FaEdit />
                      </button>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-4xl font-bold">
                      +
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCustomPose}
                    className={`opacity-0 w-full h-full absolute inset-0 cursor-pointer ${
                      customPoseImage ? 'pointer-events-none' : ''
                    }`}
                  />
                  <div className="flex items-center justify-center text-center my-24">
                    <p className="font-semibold absolute bottom-0 mb-2 ml-2 text-white text-sm bg-black bg-opacity-50 rounded px-1 ">
                      Custom Pose
                    </p>
                  </div>
                </div>
              )}

            {searchedLoras.length === 0 && (
              <div className="text-center text-gray-300 my-auto p-8">
                No Poses found. Try reducing your search terms.
              </div>
            )}

            {searchedLoras
              .filter((image) => {
                // HIDE "Default" only while in select mode (multi-pose select)
                if (
                  supportsMultiPose &&
                  selectMode &&
                  image.title === 'Default'
                )
                  return false

                if (image.hideToFree && (!user || !isPlusOrHigher)) return false
                if (image.hideToReviewer && subscription?.source === 'Reviewer')
                  return false
                if (
                  image.enabled &&
                  !image.enabled.includes(poseListModel(model))
                )
                  return false
                return true
              })
              .sort((a, b) => {
                if (a.title === 'Default') return -1
                if (b.title === 'Default') return 1
                return a.title.localeCompare(b.title)
              })
              .map((image) => {
                const isMultiSelected =
                  supportsMultiPose &&
                  selectMode &&
                  lorasList.includes(image.title)
                const isTileSelected = supportsMultiPose
                  ? selectMode
                    ? isMultiSelected
                    : image === selectedImage
                  : image === selectedImage

                return (
                  <div
                    key={image.title}
                    className={`m-1 border-2 relative rounded-xl transition-all duration-200 ease-in-out hover:scale-[1.03] cursor-pointer
        ${
          isTileSelected
            ? 'border-white shadow-outline-blue'
            : canSelectImage(image.plan)
              ? 'border-transparent hover:shadow-lg hover:border-gray-800 '
              : 'border-transparent'
        }`}
                    onClick={(e) => handleImageClick(e, image)}
                  >
                    {/* NEW: iOS-style checkbox in the top-right corner when selecting */}
                    {supportsMultiPose &&
                      selectMode &&
                      canSelectImage(image.plan) && (
                        <div className="absolute top-2 right-2 z-10">
                          <button
                            aria-pressed={isMultiSelected}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              toggleLoraInList(image.title)
                            }}
                            className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                              isMultiSelected
                                ? 'bg-[var(--main-bg-color)] border-[var(--main-bg-color)]'
                                : 'border-white bg-black bg-opacity-40'
                            }`}
                          >
                            {isMultiSelected && (
                              <FaCheck className="text-white text-xs" />
                            )}
                          </button>
                        </div>
                      )}

                    {image.videoSrc ? (
                      <video
                        src={image.videoSrc}
                        className={`w-full h-full object-cover rounded-xl ${
                          canSelectImage(image.plan) ? '' : 'opacity-80'
                        }`}
                        loop
                        autoPlay
                        muted
                        playsInline
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        preload="auto"
                      />
                    ) : (
                      <img
                        src={poseThumb(image, model)}
                        alt={image.title}
                        className={`w-full h-full object-cover rounded-xl ${
                          canSelectImage(image.plan) ? '' : 'opacity-80'
                        }`}
                        onError={(e) => {
                          // Mirror the primary src's model awareness. This used to be
                          // `image.src || image.animeSrc` for every model (via two identical
                          // branches), so a pose with no ponyAnime*/<title>.webp fell back to
                          // `src` — the legacy PHOTOREAL thumbnail — and anime models showed
                          // realistic art. Prefer the anime asset when the model is anime.
                          e.target.src = poseOwnThumb(image, model)
                        }}
                      />
                    )}

                    {!canSelectImage(image.plan) ? (
                      <div
                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded-xl border-2"
                        style={{ borderColor: getColorForPlan(image.plan) }}
                      >
                        <span className="text-gray-300 flex items-center opacity-60 font-semibold">
                          <FaLock className="mr-1" /> {image.plan}
                        </span>

                        <p className="font-semibold absolute bottom-0 mb-2 ml-2 text-gray-300 text-sm bg-black bg-opacity-50 rounded px-1">
                          {t(image.title)}
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <p className="font-semibold absolute bottom-0 mb-2 ml-2 text-white text-sm bg-black bg-opacity-50 rounded px-1">
                          {t(image.title)}
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
          </main>
        </div>
      </div>
    </Portal>
  )
}

export default LoraModal
